/**
 * @author Jorge Casas
 * @creationDate 10/10/2025
 * @path src/components/dashboard/ChartAccountsTotal.js
 */

import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import { translate } from 'react-i18next';
import PropTypes from 'prop-types';

import { Bar } from 'react-chartjs-2';
import Chart from 'chart.js';
import 'chartjs-plugin-datalabels';
import * as Common from 'common/components';

let ROUNDED_PATCH_DONE = false;
let FANCY_PLUGINS_PATCHED = false;
let AUX_VIS_PATCHED = false;

function patchRoundedBarsOnce() {
  if (ROUNDED_PATCH_DONE) return;
  if (!Chart || !Chart.elements || !Chart.elements.Rectangle) return;
  ROUNDED_PATCH_DONE = true;

  if (!Chart.defaults.global.__anepsaTailwindTweaks) {
    Chart.defaults.global.defaultFontFamily =
      'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji"';
    Chart.defaults.global.defaultFontColor = '#334155';
    Chart.defaults.global.__anepsaTailwindTweaks = true;
  }

  Chart.elements.Rectangle.prototype.draw = function () {
    const ctx = this._chart.ctx;
    const vm = this._view;

    const left = vm.x - vm.width / 2;
    const right = vm.x + vm.width / 2;
    const top = vm.y;
    const bottom = vm.base;

    const radius = Math.max(
      0,
      Math.min(8, Math.min(Math.abs(right - left) / 2, Math.abs(bottom - top) / 2))
    );
    const r = radius;

    const ds = this._chart.config.data.datasets[this._datasetIndex] || {};
    const isHL =
      Array.isArray(ds.extraHighlightIndices) && ds.extraHighlightIndices.indexOf(this._index) > -1;

    ctx.save();
    ctx.shadowColor = 'rgba(15, 23, 42, 0.12)';
    ctx.shadowBlur = isHL ? 18 : 8;
    ctx.shadowOffsetY = 2;

    ctx.beginPath();
    ctx.moveTo(left, bottom);
    ctx.lineTo(left, top + r);
    ctx.quadraticCurveTo(left, top, left + r, top);
    ctx.lineTo(right - r, top);
    ctx.quadraticCurveTo(right, top, right, top + r);
    ctx.lineTo(right, bottom);
    ctx.closePath();

    ctx.fillStyle = vm.backgroundColor;
    ctx.fill();

    if (vm.borderWidth) {
      ctx.strokeStyle = vm.borderColor;
      ctx.lineWidth = vm.borderWidth;
      ctx.stroke();
    }
    ctx.restore();
  };
}

function patchFancyPluginsOnce() {
  if (FANCY_PLUGINS_PATCHED) return;
  FANCY_PLUGINS_PATCHED = true;

  Chart.plugins.register({
    afterDraw(chart) {
      const active = chart.tooltip && chart.tooltip._active && chart.tooltip._active[0];
      if (!active || !chart.chartArea) return;
      const ctx = chart.ctx;
      const x = active.tooltipPosition().x;
      const ca = chart.chartArea;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, ca.top);
      ctx.lineTo(x, ca.bottom);
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(51,65,85,0.22)';
      ctx.stroke();
      ctx.restore();
    },
  });

  Chart.plugins.register({
    afterDatasetsDraw(chart) {
      const ds = chart.config.data.datasets && chart.config.data.datasets[0];
      if (!ds || typeof ds.__avg !== 'number') return;
      const yScale = chart.scales['y-axis-0'] || chart.scales['y-axis-1'];
      if (!yScale) return;

      const y = yScale.getPixelForValue(ds.__avg);
      const ca = chart.chartArea;
      const ctx = chart.ctx;

      ctx.save();
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(99,102,241,0.6)';
      ctx.beginPath();
      ctx.moveTo(ca.left, y);
      ctx.lineTo(ca.right, y);
      ctx.stroke();
      ctx.setLineDash([]);

      const fmt = function (n) {
        try {
          return new Intl.NumberFormat('es-MX').format(n);
        } catch (_) {
          return String(n);
        }
      };
      const label = 'Promedio ' + fmt(ds.__avg);

      ctx.font = '11px Inter, system-ui, sans-serif';
      const padX = 8,
        h = 20;
      const w = ctx.measureText(label).width + padX * 2;
      const x = ca.right - w - 6;
      const yTop = y - h - 4;

      ctx.fillStyle = 'rgba(15,23,42,0.92)';
      ctx.fillRect(x, yTop, w, h);
      ctx.fillStyle = '#fff';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x + padX, yTop + h / 2);
      ctx.restore();
    },
  });
}

function patchAuxVisualsOnce() {
  if (AUX_VIS_PATCHED) return;
  AUX_VIS_PATCHED = true;

  Chart.plugins.register({
    beforeDraw(chart) {
      const ca = chart.chartArea;
      if (!ca) return;
      const opts = chart.config.options || {};
      const plotBorder = opts.plotBorder || {};

      if (plotBorder.enabled) {
        const ctx = chart.ctx;
        const color = plotBorder.color || 'rgba(2,6,23,0.08)';
        const width = plotBorder.width || 1;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.strokeRect(ca.left + 0.5, ca.top + 0.5, ca.right - ca.left - 1, ca.bottom - ca.top - 1);
        ctx.restore();
      }
    },
  });
}
class ChartAccountsTotal extends Component {
  static propTypes = { data: PropTypes.array.isRequired, t: PropTypes.func };

  constructor(props) {
    super(props);
    patchRoundedBarsOnce();
    patchFancyPluginsOnce();
    patchAuxVisualsOnce();
  }

  state = { mode: 'abs' };

  shouldComponentUpdate = (nextProps, nextState) =>
    !isEqual(nextProps.data, this.props.data) || !isEqual(nextState, this.state);

  PALETTE = [
    '#0ea5e9',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#14b8a6',
    '#eab308',
    '#f43f5e',
    '#3b82f6',
    '#84cc16',
    '#06b6d4',
    '#a78bfa',
    '#10b981',
    '#fb7185',
    '#64748b',
    '#f97316',
  ];
  hexToRgba = (hex, a) => {
    try {
      const h = hex.replace('#', '');
      const n = parseInt(h, 16);
      const r = (n >> 16) & 255,
        g = (n >> 8) & 255,
        b = n & 255;
      return 'rgba(' + r + ',' + g + ',' + b + ',' + (a == null ? 1 : a) + ')';
    } catch (e) {
      return hex;
    }
  };
  hash = (s) => {
    let h = 0,
      str = String(s || '');
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };
  colorKeyFor = (acc) => acc && (acc._id || acc.id || acc.code || acc.name || '');

  fmt = (n) => {
    try {
      return new Intl.NumberFormat('es-MX').format(n);
    } catch (_) {
      return String(n);
    }
  };
  fmtCompact = (n) => {
    try {
      return new Intl.NumberFormat('es-MX', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(n);
    } catch (_) {
      return String(n);
    }
  };

  chartRef = null;
  setChartRef = (ref) => {
    this.chartRef = null;
    if (ref) this.chartRef = ref.chartInstance ? ref.chartInstance : ref.chart ? ref.chart : ref;
  };

  downloadPNG = () => {
    const c = this.chartRef;
    if (!c || !c.toBase64Image) return;
    const url = c.toBase64Image();
    const a = document.createElement('a');
    a.href = url;
    a.download = 'activos_por_cuenta.png';
    a.click();
  };

  getChartData = (canvas) => {
    const ctx = canvas.getContext('2d');
    const H = canvas.height;

    const itemsAll = (this.props.data || []).map((acc) => {
      const rawName = String(acc && acc.name != null ? acc.name : '');
      const label = this.props.t ? this.props.t(rawName) : rawName;
      const value = acc && acc.total != null ? acc.total : 0;
      const key = this.colorKeyFor(acc);
      const base = this.PALETTE[this.hash(key) % this.PALETTE.length];
      return { label, value, base };
    });

    itemsAll.sort(function (a, b) {
      return (b.value || 0) - (a.value || 0);
    });

    const sumAll =
      itemsAll.reduce(function (a, b) {
        return a + (b.value || 0);
      }, 0) || 1;

    const labels = [];
    const values = [];
    const bgs = [];
    const borders = [];
    const hoverBgs = [];

    for (var i = 0; i < itemsAll.length; i++) {
      var it = itemsAll[i];
      labels.push(it.label);
      var v = it.value;
      values.push(this.state.mode === 'pct' ? +((v * 100) / sumAll).toFixed(1) : v);

      var grad = ctx.createLinearGradient(0, H, 0, 0);
      grad.addColorStop(0, this.hexToRgba(it.base, 0.55));
      grad.addColorStop(1, this.hexToRgba(it.base, 0.92));
      bgs.push(grad);

      var strong = this.hexToRgba(it.base, 1);
      borders.push(strong);
      hoverBgs.push(strong);
    }

    const topIdx = Array.from({ length: Math.min(3, itemsAll.length) }, function (_, idx) {
      return idx;
    });

    return {
      labels,
      datasets: [
        {
          label:
            this.state.mode === 'pct'
              ? '% del total'
              : this.props.t
              ? this.props.t('dashboard.assetsTotal')
              : 'Número de activos',
          data: values,
          backgroundColor: bgs,
          borderColor: borders,
          hoverBackgroundColor: hoverBgs,
          borderWidth: 0,
          maxBarThickness: 28,
          categoryPercentage: 0.7,
          barPercentage: 0.9,
          extraTopIndices: topIdx,
          extraHighlightIndices: itemsAll.length ? [0] : [],
          __sumAll: sumAll,
          __avg: itemsAll.length
            ? itemsAll.reduce(function (a, b) {
                return a + (b.value || 0);
              }, 0) / itemsAll.length
            : 0,
          __mapRaw: itemsAll,
          __mode: this.state.mode,
        },
      ],
    };
  };

  render() {
    const t =
      this.props.t ||
      function (s) {
        return s;
      };

    const totalAll = (this.props.data || []).reduce(function (a, b) {
      return a + (b && b.total ? b.total : 0);
    }, 0);
    const cuentas = (this.props.data || []).length;
    const ordered = [].slice.call(this.props.data || []).sort(function (a, b) {
      return (b.total || 0) - (a.total || 0);
    });
    const top1 = ordered[0];
    const topShare = top1 ? (((top1.total || 0) / Math.max(totalAll, 1)) * 100).toFixed(1) : '0.0';

    return (
      <div className="col-lg-12">
        <Common.Portlet title={t('dashboard.totalByAccounts')}>
          {/* Toolbar */}
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap chart-toolbar">
            <div className="d-flex align-items-center flex-wrap">
              <span className="badge badge-light mr-2 mb-2">Total: {this.fmt(totalAll)}</span>
              <span className="badge badge-light mr-2 mb-2">Cuentas: {cuentas}</span>
              <span className="badge badge-light mr-2 mb-2">Top concentra: {topShare}%</span>
            </div>

            <div className="d-flex align-items-center flex-wrap mt-2 mt-sm-0" style={{ gap: 10 }}>
              {/* Segmented toggle ABS/% */}
              <div
                className="segmented-toggle mb-2"
                role="group"
                aria-label="Modo de visualización"
              >
                <button
                  type="button"
                  className={'segmented-btn ' + (this.state.mode === 'abs' ? 'is-active' : '')}
                  aria-pressed={this.state.mode === 'abs'}
                  onClick={() => this.setState({ mode: 'abs' })}
                  title="Mostrar valores absolutos"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M5 9h14M5 15h14M9 3l-2 18M17 3l-2 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>ABS</span>
                </button>

                <button
                  type="button"
                  className={'segmented-btn ' + (this.state.mode === 'pct' ? 'is-active' : '')}
                  aria-pressed={this.state.mode === 'pct'}
                  onClick={() => this.setState({ mode: 'pct' })}
                  title="Mostrar porcentaje del total"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M19 5L5 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle cx="7.5" cy="6.5" r="2" stroke="currentColor" strokeWidth="2" />
                    <circle cx="16.5" cy="17.5" r="2" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>%</span>
                </button>
              </div>

              <button
                type="button"
                className="download-btn--pro mb-2"
                aria-label="Descargar gráfico en PNG"
                onClick={this.downloadPNG}
                title="Descargar imagen PNG"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M12 3v10m0 0l-4-4m4 4l4-4M5 21h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="btn-text" aria-hidden="true">
                  Descargar PNG
                </span>
              </button>
            </div>
          </div>

          <div style={{ position: 'relative', height: 380, maxHeight: '65vh' }}>
            <Bar
              ref={this.setChartRef}
              data={this.getChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 24, right: 12, left: 8, bottom: 8 } },
                animation: { duration: 900, easing: 'easeOutQuart' },
                hover: { mode: 'index', intersect: false },
                onHover: (e) => {
                  e.target.style.cursor = 'default';
                },

                plotBorder: { enabled: true, color: 'rgba(2,6,23,0.08)', width: 1 },

                plugins: {
                  datalabels: {
                    display: function (ctx) {
                      const arr = ctx.dataset.extraTopIndices || [];
                      return arr.indexOf(ctx.dataIndex) > -1;
                    },
                    anchor: 'end',
                    align: 'end',
                    offset: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(15,23,42,0.88)',
                    color: '#fff',
                    padding: { top: 3, bottom: 3, left: 6, right: 6 },
                    formatter: function (v, ctx) {
                      var mode = ctx && ctx.dataset && ctx.dataset.__mode;
                      if (mode === 'pct') return v + '%';
                      try {
                        return new Intl.NumberFormat('es-MX').format(v);
                      } catch (e) {
                        return v;
                      }
                    },
                    font: { size: 11, weight: '600' },
                  },
                },
                elements: { rectangle: { borderSkipped: 'bottom' } },
                legend: { labels: { boxWidth: 14, usePointStyle: true, fontColor: '#334155' } },
                tooltips: {
                  backgroundColor: 'rgba(15, 23, 42, 0.92)',
                  titleFontStyle: 'normal',
                  xPadding: 10,
                  yPadding: 10,
                  cornerRadius: 6,
                  mode: 'index',
                  intersect: false,
                  callbacks: {
                    title: function (tooltipItems, data) {
                      var i = tooltipItems && tooltipItems[0] ? tooltipItems[0].index : 0;
                      return data.labels[i];
                    },
                    label: (ti, data) => {
                      const ds = data.datasets[ti.datasetIndex];
                      const vShown = ds.data[ti.index];
                      if (this.state.mode === 'pct') return ' ' + vShown + '%';
                      try {
                        return ' ' + new Intl.NumberFormat('es-MX').format(vShown);
                      } catch (e) {
                        return ' ' + vShown;
                      }
                    },
                    afterLabel: (ti, data) => {
                      const ds = data.datasets[ti.datasetIndex];
                      const rawList = ds.__mapRaw || [];
                      const raw = rawList[ti.index] ? rawList[ti.index].value : 0;
                      const sumAll = ds.__sumAll || 1;
                      const pctAll = ((raw / sumAll) * 100).toFixed(1);
                      if (this.state.mode === 'pct') {
                        try {
                          return ' ' + new Intl.NumberFormat('es-MX').format(raw) + ' (ABS)';
                        } catch (e) {
                          return ' ' + raw + ' (ABS)';
                        }
                      } else {
                        return ' ' + pctAll + '% del total';
                      }
                    },
                  },
                },
                scales: {
                  xAxes: [
                    {
                      ticks: {
                        autoSkip: false,
                        minRotation: 60,
                        maxRotation: 60,
                        fontSize: 11,
                        fontColor: '#334155',
                        callback: function (value) {
                          const s = String(value || '');
                          const max = 18;
                          return s.length > max ? s.substr(0, max - 1) + '…' : s;
                        },
                      },
                      gridLines: { display: false },
                    },
                  ],
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        padding: 8,
                        fontSize: 11,
                        fontColor: '#475569',
                        callback: (value) =>
                          this.state.mode === 'pct' ? value + '%' : this.fmtCompact(value),
                      },
                      gridLines: {
                        drawBorder: false,
                        drawTicks: false,
                        color: 'rgba(148,163,184,0.16)',
                        zeroLineColor: 'rgba(2,6,23,0.22)',
                        borderDash: [4, 4],
                        zeroLineBorderDash: [4, 4],
                      },
                    },
                  ],
                },
              }}
            />
          </div>
        </Common.Portlet>
      </div>
    );
  }
}

export default translate()(ChartAccountsTotal);



// WEBPACK FOOTER //
// src/components/dashboard/ChartAccountsTotal.js